import { useCallback, useMemo, useRef } from 'react'
import type { Connection } from '../hooks/useConnections'

interface ConnectionsListProps {
  connections: Connection[]
  loading: boolean
  searchQuery: string
  onSearchChange: (query: string) => void
  searchLoading: boolean
  onSelect: (connection: Connection) => void
  hasMore?: boolean
  paginating?: boolean
  onLoadMore?: () => void
}

export default function ConnectionsList({
  connections,
  loading,
  searchQuery,
  onSearchChange,
  searchLoading,
  onSelect,
  hasMore,
  paginating,
  onLoadMore,
}: ConnectionsListProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const isLoading = searchQuery.trim() ? searchLoading : loading
  const isSearching = !!searchQuery.trim()

  // Group by recency label in default view, flat list when searching
  const grouped = useMemo(() => {
    if (isSearching) return null
    const map = new Map<string, Connection[]>()
    for (const conn of connections) {
      const key = normalizeGroupLabel(conn.group)
      const list = map.get(key) || []
      list.push(conn)
      map.set(key, list)
    }
    return [...map.entries()]
  }, [connections, isSearching])

  const handleScroll = useCallback(() => {
    if (!hasMore || paginating || !onLoadMore || !scrollRef.current || isSearching) return
    const el = scrollRef.current
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
      onLoadMore()
    }
  }, [hasMore, paginating, onLoadMore, isSearching])

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Search bar */}
      <div className="border-b border-zinc-100 px-4 py-3">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search connections..."
          className={`w-full rounded-lg px-3 py-[7px] text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none ${
            searchQuery ? 'bg-white ring-1 ring-blue-400' : 'bg-zinc-100'
          }`}
        />
      </div>

      {/* List */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto" onScroll={handleScroll}>
        {isLoading && connections.length === 0 ? (
          <div className="px-4 pt-3">
            {[0.9, 0.7, 0.5, 0.35, 0.2].map((opacity, i) => (
              <div key={i} className="flex items-center gap-3 py-2.5" style={{ opacity }}>
                <div className="h-9 w-9 shrink-0 animate-pulse rounded-full bg-zinc-200" />
                <div className="flex flex-1 flex-col gap-1.5">
                  <div className="h-2.5 animate-pulse rounded bg-zinc-200" style={{ width: `${45 + (i % 3) * 15}%` }} />
                  <div className="h-2 animate-pulse rounded bg-zinc-100" style={{ width: `${60 + (i % 3) * 10}%` }} />
                </div>
              </div>
            ))}
          </div>
        ) : connections.length === 0 ? (
          <div className="flex flex-1 items-center justify-center p-6 text-center">
            <div>
              <p className="text-2xl text-zinc-200">👥</p>
              <p className="mt-2 text-xs text-zinc-400">
                {searchQuery.trim() ? `No connections found for "${searchQuery}"` : 'No connections'}
              </p>
            </div>
          </div>
        ) : grouped ? (
          // Grouped view (default)
          grouped.map(([title, conns]) => (
            <div key={title}>
              <div className="sticky top-0 z-10 bg-white/95 px-4 py-1.5 backdrop-blur-sm">
                <span className="text-[10px] font-medium uppercase tracking-wider text-zinc-400">{title}</span>
              </div>
              {conns.map((conn) => (
                <ConnectionRow key={conn.profileUrn} connection={conn} onSelect={onSelect} />
              ))}
            </div>
          ))
        ) : (
          // Flat search results
          connections.map((conn) => (
            <ConnectionRow key={conn.profileUrn} connection={conn} onSelect={onSelect} />
          ))
        )}
        {paginating && (
          <div className="py-3 text-center text-xs text-zinc-400">Loading more...</div>
        )}
      </div>
    </div>
  )
}

function ConnectionRow({ connection, onSelect }: { connection: Connection; onSelect: (c: Connection) => void }) {
  return (
    <button
      onClick={() => onSelect(connection)}
      className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-zinc-50"
    >
      {connection.profileImage ? (
        <img src={connection.profileImage} alt="" className="h-9 w-9 shrink-0 rounded-full" />
      ) : (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-[10px] font-semibold text-zinc-500">
          {getInitials(connection.name)}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-medium text-zinc-900">{connection.name}</p>
        {connection.headline && (
          <p className="truncate text-[11px] text-zinc-500">{connection.headline}</p>
        )}
      </div>
    </button>
  )
}

function getInitials(name: string): string {
  return name.split(' ').map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase()
}

// Map LinkedIn's "Connected X ago" labels to readable time buckets
function normalizeGroupLabel(label?: string): string {
  if (!label) return 'Connections'
  const lower = label.toLowerCase()

  if (lower.includes('today') || lower.includes('yesterday')) return 'Today'
  if (lower.includes('this week') || lower.includes('last week') || lower.includes('1 week')) return 'Last 7 days'
  if (lower.includes('2 week')) return 'Last 14 days'
  if (lower.includes('3 week') || lower.includes('this month') || lower.includes('1 month')) return 'This month'

  // Parse "Connected N months/years ago" into specific labels
  const monthMatch = lower.match(/(\d+)\s*months?\s*ago/)
  if (monthMatch) {
    const n = parseInt(monthMatch[1], 10)
    if (n <= 1) return 'This month'
    if (n <= 3) return 'Last 3 months'
    if (n <= 6) return 'Last 6 months'
    // Beyond 6 months: derive the approximate month name
    const d = new Date()
    d.setMonth(d.getMonth() - n)
    return d.toLocaleString('default', { month: 'long', year: 'numeric' })
  }

  const yearMatch = lower.match(/(\d+)\s*years?\s*ago/)
  if (yearMatch) {
    const n = parseInt(yearMatch[1], 10)
    return `${new Date().getFullYear() - n}`
  }

  if (lower.includes('month')) return 'This month'
  if (lower.includes('year')) return `${new Date().getFullYear() - 1}`

  return 'Earlier'
}
